// src/components/Matches/MatchesDashboard.jsx
import React, { useState, useEffect } from 'react';
import { Card, Badge, Button, Row, Col, Modal, Form, Spinner, Toast, ToastContainer } from 'react-bootstrap';
import { MessageSquare, Calendar, X, Clock, DollarSign, MapPin, Award, Check } from 'lucide-react';
import { motion } from 'framer-motion';
import matchingService from '../../services/matchingService';
import FlippableListingCard from '../Profile/FlippableListingCard';
import './MatchesDashboard.css';

const MatchesDashboard = ({ matches, onRefresh }) => {
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showNegotiationModal, setShowNegotiationModal] = useState(false);
  const [showListingPreview, setShowListingPreview] = useState(false);
  const [previewListing, setPreviewListing] = useState(null);
  const [notes, setNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [negotiationMatch, setNegotiationMatch] = useState(null);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  
  // Mark matches as viewed when dashboard is opened
  useEffect(() => {
    const markMatchesAsViewed = async () => {
      try {
        console.log('Processing matches in dashboard:', matches);
        const pendingMatches = matches.filter(match => 
          !match.viewedByProvider && match.status === 'pending'
        );
        
        console.log('Pending matches to mark as viewed:', pendingMatches);
        if (pendingMatches.length > 0) {
          await Promise.all(pendingMatches.map(match => {
            console.log('Marking match as viewed:', match._id);
            return matchingService.markMatchViewed(match._id);
          }));
          if (onRefresh) onRefresh();
        }
      } catch (error) {
        console.error('Failed to mark matches as viewed:', error);
      }
    };
    
    if (matches && matches.length > 0) {
      markMatchesAsViewed();
    }
  }, [matches, onRefresh]);

  if (!matches || matches.length === 0) {
    return (
      <div className="empty-matches-container text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="empty-state-icon">
            <MessageSquare size={48} />
          </div>
          <h4>No matches found yet</h4>
          <p className="text-muted">Swipe right on listings you're interested in to create matches!</p>
          <Button 
            variant="primary" 
            className="mt-3 premium-btn"
            onClick={onRefresh}
          >
            Refresh Matches
          </Button>
        </motion.div>
      </div>
    );
  }

  // Notes modal handlers - kept for future use if needed
  // Currently not used but preserved for potential future implementation
  const handleShowDetails = (match) => {
    setSelectedMatch(match);
    setNotes(match.notes || '');
    setShowDetailsModal(true);
  };

  const handleCloseDetails = () => {
    setShowDetailsModal(false);
    setSelectedMatch(null);
  };
  
  // Listing preview modal handlers
  const handleShowListingPreview = (match) => {
    setPreviewListing(match.listingId);
    setShowListingPreview(true);
  };
  
  const handleCloseListingPreview = () => {
    setShowListingPreview(false);
    setPreviewListing(null);
  };

  const handleSaveNotes = async () => {
    if (!selectedMatch || processing) return;
    
    setProcessing(true);
    try {
      await matchingService.addMatchNotes(selectedMatch._id, notes);
      if (onRefresh) onRefresh();
      handleCloseDetails();
    } catch (error) {
      console.error('Failed to save notes:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleShowNegotiationConfirm = (match) => {
    setNegotiationMatch(match);
    setShowNegotiationModal(true);
  };

  const handleCloseNegotiationConfirm = () => {
    setShowNegotiationModal(false);
    setNegotiationMatch(null);
  };

  const handleConfirmNegotiation = async () => {
    if (!negotiationMatch) return;
    
    setProcessing(true);
    try {
      console.log(`Starting negotiations for match ${negotiationMatch._id}`);
      await matchingService.updateMatchStatus(negotiationMatch._id, 'negotiating');
      
      // Show success toast notification
      setToastMessage(`Negotiations started for ${negotiationMatch.listingId?.title || 'this position'}. Contact information has been shared via email.`);
      setShowSuccessToast(true);
      
      if (onRefresh) onRefresh();
      handleCloseNegotiationConfirm();
    } catch (error) {
      console.error('Failed to start negotiations:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleUpdateStatus = async (matchId, status) => {
    // For statuses other than negotiating, update directly
    // For negotiating, show confirmation modal first
    if (status === 'negotiating') {
      const match = matches.find(m => m._id === matchId);
      handleShowNegotiationConfirm(match);
      return;
    }
    
    setProcessing(true);
    try {
      console.log(`Updating match ${matchId} to status: ${status}`);
      await matchingService.updateMatchStatus(matchId, status);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Failed to update status:', error);
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'negotiating':
        return <Badge className="status-badge accepted">In Negotiation</Badge>;
      case 'contacted':
        return <Badge className="status-badge accepted">Contacted</Badge>;
      case 'hired':
        return <Badge className="status-badge accepted">Hired</Badge>;
      case 'declined':
        return <Badge className="status-badge declined">Declined</Badge>;
      case 'closed':
        return <Badge className="status-badge declined">Closed</Badge>;
      case 'pending':
        return <Badge className="status-badge pending">
          <span className="pulse-dot"></span> Pending
        </Badge>;
      default:
        return <Badge className="status-badge new">
          <span className="pulse-dot"></span> New Match
        </Badge>;
    }
  };

  const renderNegotiationDetails = (match) => {
    if (match.status !== 'negotiating') return null;
    
    return (
      <div className="negotiation-info-panel">
        <h6 className="negotiation-info-title">Negotiation Started</h6>
        <p className="negotiation-info-message">
          Contact information has been shared via email. You can reach out to the facility directly.
        </p>
        <Button 
          variant="outline-primary" 
          size="sm" 
          className="email-btn"
          href={`mailto:${match.facilityEmail || 'contact@facility.com'}?subject=Regarding ${match.listingId?.title || 'Position'}`}
        >
          <MessageSquare size={14} className="me-1" /> Send Email
        </Button>
      </div>
    );
  };

  return (
    <div className="matches-dashboard">
      <div className="matches-header">
        <h3>Your Matches <span className="match-count">{matches.length}</span></h3>
        <Button 
          variant="outline-primary" 
          size="sm" 
          className="refresh-btn"
          onClick={onRefresh}
          disabled={processing}
        >
          {processing ? <Spinner size="sm" animation="border" /> : 'Refresh'}
        </Button>
      </div>
      
      <Row className="match-cards g-4">
        {matches.map((match) => (
          <Col xs={12} md={6} lg={4} key={match._id}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <Card className="match-card">
                <Card.Body>
                <div className="card-header-container">
                  <div className="card-title-area">
                    <Card.Title>
                      {match.listingId?.title || match.listingId?.facilityName || 'Facility'}
                    </Card.Title>
                  </div>
                  <div className="card-badge-area">
                    {getStatusBadge(match.status)}
                  </div>
                </div>
                
                <div className="match-details">
                  {renderNegotiationDetails(match)}
                  <div className="match-detail-item">
                    <MapPin size={16} className="icon" />
                    <span>{match.listingId?.location || match.listingId?.facility?.location || 'Unknown location'}</span>
                  </div>
                  <div className="match-detail-item">
                    <Calendar size={16} className="icon" />
                    <span>
                      {match.listingId?.assignment?.startDate 
                        ? new Date(match.listingId.assignment.startDate).toLocaleDateString() 
                        : match.listingId?.startDate 
                          ? new Date(match.listingId.startDate).toLocaleDateString()
                          : 'Flexible'}
                    </span>
                  </div>
                  <div className="match-detail-item">
                    <Clock size={16} className="icon" />
                    <span>
                      {match.listingId?.assignment?.types?.join(', ') || 
                       match.listingId?.duration + ' ' + (match.listingId?.durationType || 'period') || 
                       'Unknown schedule'}
                    </span>
                  </div>
                  <div className="match-detail-item">
                    <DollarSign size={16} className="icon" />
                    <span>
                      ${(match.listingId?.compensation?.amount || 
                         match.listingId?.payRate || '0').toLocaleString()}
                      {match.listingId?.compensation?.period ? '/' + match.listingId.compensation.period : '/year'}
                    </span>
                  </div>
                </div>
                
                <div className="match-actions mt-3">
                  <Button 
                    variant="primary" 
                    size="sm"
                    className="detail-btn premium-btn"
                    onClick={() => handleShowListingPreview(match)}
                  >
                    <Award size={16} />
                    <span>Listing Preview</span>
                  </Button>
                  
                  {match.status === 'pending' && (
                    <div className="decision-buttons">
                      <Button 
                        variant="danger" 
                        size="sm"
                        className="action-btn decline-btn"
                        onClick={() => handleUpdateStatus(match._id, 'declined')}
                        disabled={processing}
                      >
                        <X size={16} />
                        <span>Decline</span>
                      </Button>
                      <Button 
                        variant="success" 
                        size="sm"
                        className="action-btn accept-btn"
                        onClick={() => handleUpdateStatus(match._id, 'negotiating')}
                        disabled={processing}
                      >
                        <MessageSquare size={16} />
                        <span>Negotiate</span>
                      </Button>
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>
            </motion.div>
          </Col>
        ))}
      </Row>
      
      {/* Match Details Modal */}
      <Modal show={showDetailsModal} onHide={handleCloseDetails} centered dialogClassName="premium-modal">
        <Modal.Header closeButton className="premium-modal-header">
          <Modal.Title>
            <Award size={20} className="me-2" /> Match Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="premium-modal-body">
          {selectedMatch && (
            <>
              <div className="facility-header">
                <h4>{selectedMatch.listingId?.title || selectedMatch.listingId?.facilityName || 'Position'}</h4>
                {getStatusBadge(selectedMatch.status)}
              </div>
              
              <p className="facility-description">{selectedMatch.listingId?.description || 'No description available'}</p>
              
              <div className="match-full-details">
                <div className="detail-row">
                  <strong>Facility:</strong>
                  <span>{selectedMatch.listingId?.facility?.name || selectedMatch.listingId?.facility || 'Unknown'}</span>
                </div>
                <div className="detail-row">
                  <strong>Location:</strong>
                  <span>{selectedMatch.listingId?.location || selectedMatch.listingId?.facility?.location || 'Unknown location'}</span>
                </div>
                <div className="detail-row">
                  <strong>Compensation:</strong>
                  <span>
                    ${(selectedMatch.listingId?.compensation?.amount || 
                       selectedMatch.listingId?.payRate || '0').toLocaleString()}
                    {selectedMatch.listingId?.compensation?.period ? '/' + selectedMatch.listingId.compensation.period : '/year'}
                  </span>
                </div>
                <div className="detail-row">
                  <strong>Contract Type:</strong>
                  <span>{selectedMatch.listingId?.compensation?.contractType || 'Not specified'}</span>
                </div>
                <div className="detail-row">
                  <strong>Start Date:</strong>
                  <span>
                    {selectedMatch.listingId?.assignment?.startDate 
                      ? new Date(selectedMatch.listingId.assignment.startDate).toLocaleDateString() 
                      : selectedMatch.listingId?.startDate 
                        ? new Date(selectedMatch.listingId.startDate).toLocaleDateString()
                        : 'Flexible'}
                  </span>
                </div>
                <div className="detail-row">
                  <strong>Assignment Types:</strong>
                  <span>{selectedMatch.listingId?.assignment?.types?.join(', ') || 'Not specified'}</span>
                </div>
                {selectedMatch.listingId?.clinical?.requiredCaseTypes?.length > 0 && (
                  <div className="detail-row">
                    <strong>Required Case Types:</strong>
                    <span>{selectedMatch.listingId.clinical.requiredCaseTypes.join(', ')}</span>
                  </div>
                )}
                <div className="detail-row">
                  <strong>Status:</strong>
                  <span>{getStatusBadge(selectedMatch.status)}</span>
                </div>
                <div className="detail-row">
                  <strong>Matched On:</strong>
                  <span>{new Date(selectedMatch.updatedAt).toLocaleDateString()}</span>
                </div>
              </div>
              
              <Form.Group className="mt-3">
                <Form.Label>Notes</Form.Label>
                <Form.Control 
                  as="textarea" 
                  rows={3} 
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  disabled={processing}
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleCloseDetails}>
            Close
          </Button>
          <Button 
            variant="primary" 
            className="premium-btn" 
            onClick={handleSaveNotes} 
            disabled={processing}
          >
            {processing ? <Spinner animation="border" size="sm" /> : 'Save Notes'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Negotiation Confirmation Modal */}
      <Modal show={showNegotiationModal} onHide={handleCloseNegotiationConfirm} centered dialogClassName="premium-modal">
        <Modal.Header closeButton className="premium-modal-header">
          <Modal.Title>
            <MessageSquare size={20} className="me-2" /> Start Negotiations
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="premium-modal-body">
          {negotiationMatch && (
            <>
              <div className="alert alert-info">
                <h5 className="alert-heading">Important Information</h5>
                <p>
                  By starting negotiations, you agree to share your contact information with the facility/group and they will also share their contact information with you.
                </p>
                <hr />
                <p className="mb-0">
                  <strong>Your information to be shared:</strong>
                  <ul>
                    <li>Full name</li>
                    <li>Email address</li>
                    <li>Phone number</li>
                  </ul>
                </p>
              </div>
              
              <p>Are you sure you want to start negotiations for this position?</p>
              <h5>{negotiationMatch.listingId?.title || 'Facility Position'}</h5>
              <p>{negotiationMatch.listingId?.facility?.name || 'Unknown facility'}</p>
              
              <div className="d-flex justify-content-end gap-2 mt-4">
                <Button 
                  variant="outline-secondary" 
                  onClick={handleCloseNegotiationConfirm}
                  disabled={processing}
                >
                  Cancel
                </Button>
                <Button 
                  variant="success" 
                  onClick={handleConfirmNegotiation}
                  disabled={processing}
                >
                  {processing ? (
                    <>
                      <Spinner size="sm" animation="border" className="me-2" />
                      Processing...
                    </>
                  ) : (
                    'Confirm & Share Contact Info'
                  )}
                </Button>
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>

      {/* Listing Preview Modal */}
      <Modal show={showListingPreview} onHide={handleCloseListingPreview} centered dialogClassName="listing-preview-modal">
        <Modal.Header closeButton className="premium-modal-header">
          <Modal.Title>
            <Award size={20} className="me-2" /> Listing Preview
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="premium-modal-body swipe-card-preview">
          {previewListing && (
            <div className="listing-preview-container">
              <div className="flippable-card-container">
                <FlippableListingCard profile={previewListing} isCertified={true} />
              </div>
              
              <div className="preview-actions mt-3">
                <Button 
                  variant="outline-secondary" 
                  onClick={handleCloseListingPreview}
                >
                  Close Preview
                </Button>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
      
      {/* Success Toast Notification */}
      <ToastContainer className="p-3" position="top-end">
        <Toast 
          onClose={() => setShowSuccessToast(false)} 
          show={showSuccessToast} 
          delay={6000} 
          autohide 
          bg="success"
          className="negotiation-toast"
        >
          <Toast.Header closeButton>
            <Check size={18} className="me-2" />
            <strong className="me-auto">Negotiations Started</strong>
          </Toast.Header>
          <Toast.Body>
            {toastMessage}
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
};

export default MatchesDashboard;
